import { useCallback } from 'react';

import {
  ApprovalRuleCreate,
  ApprovalRulePatch,
  ApprovalRule,
  ApprovalRuleService,
  ApprovalRuleConfig,
  DefaultApprovalRulePatch
} from 'openapi';

import { useInterceptor } from 'hooks/useInterceptor';

export const useApprovalsController = () => {
  const { interceptRequest } = useInterceptor();

  const {
    getAllApprovalRules,
    createApprovalRule,
    getApprovalRuleById,
    updateApprovalRuleById,
    deleteApprovalRuleById
  } = ApprovalRuleService;

  const getAllApprovalRulesForCompany = useCallback(
    (companyId: number): Promise<ApprovalRuleConfig> =>
      interceptRequest(getAllApprovalRules, {}, companyId),
    [getAllApprovalRules]
  );

  const createApprovalRuleForCompany = useCallback(
    (companyId: number, body: ApprovalRuleCreate) =>
      interceptRequest(createApprovalRule, {}, companyId, body),
    [createApprovalRule]
  );

  const getApprovalRule = useCallback(
    (companyId: number, approvalRuleId: number) =>
      interceptRequest(getApprovalRuleById, {}, companyId, approvalRuleId),
    [getApprovalRuleById]
  );

  const updateApprovalRule = useCallback(
    (companyId: number, approvalRuleId: number, body: ApprovalRulePatch) =>
      interceptRequest(
        updateApprovalRuleById,
        {},
        companyId,
        approvalRuleId,
        body
      ),
    [updateApprovalRuleById]
  );

  const patchApprovalRule = useCallback(
    (
      companyId: number,
      approvalRuleId: number,
      body: ApprovalRulePatch
    ): Promise<ApprovalRule> =>
      interceptRequest(
        ApprovalRuleService.patchApprovalRuleById,
        {},
        companyId,
        approvalRuleId,
        body
      ),
    [ApprovalRuleService.patchApprovalRuleById]
  );

  const deleteApprovalRule = useCallback(
    (companyId: number, approvalRuleId: number) =>
      interceptRequest(deleteApprovalRuleById, {}, companyId, approvalRuleId),
    [deleteApprovalRuleById]
  );

  const patchDefaultApprovalRule = useCallback(
    (
      companyId: number,
      body: DefaultApprovalRulePatch
    ): Promise<ApprovalRule> =>
      interceptRequest(
        ApprovalRuleService.patchDefaultApprovalRule,
        {},
        companyId,
        body
      ),
    [ApprovalRuleService.patchDefaultApprovalRule]
  );

  return {
    getAllApprovalRulesForCompany,
    createApprovalRuleForCompany,
    getApprovalRule,
    updateApprovalRule,
    patchApprovalRule,
    deleteApprovalRule,
    patchDefaultApprovalRule
  };
};
